import t from "@ext/localization/locale/translate";
import type { Mode } from "@ext/markdown/elements/view/edit/components/Helpers/AddFilter";
import PropertiesScrollContainer from "@ext/properties/components/Helpers/PropertiesScrollContainer";
import PropertyButtons from "@ext/properties/components/Helpers/PropertyButtons";
import { Checkbox } from "@ui-kit/Checkbox";
import {
	DropdownMenuCheckboxItem,
	DropdownMenuItem,
	DropdownMenuRadioItem,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from "@ui-kit/Dropdown";
import { type MouseEvent, type ReactNode, useCallback, useMemo } from "react";

interface ItemProps {
	name: string;
	id?: string;
	trigger: ReactNode;
	selected?: boolean;
	value?: string[];
	values?: string[];
	mode?: Mode;
	ignoreEmpty?: boolean;
	buttons?: ReactNode;
	onClick: (value?: string | string[]) => void;
	renderer?: () => ReactNode;
}

const getCheckedState = (values: string[], selected: string[]) => {
	const selectedCount = selected?.length;
	const valuesCount = values?.length + 1;
	if (selectedCount && selectedCount === valuesCount) return false;
	if (selectedCount && valuesCount !== selectedCount) return "indeterminate";
	return true;
};

const Item = ({
	values,
	onClick,
	renderer,
	trigger,
	selected,
	value,
	name,
	id,
	mode,
	ignoreEmpty,
	buttons,
}: ItemProps) => {
	const rendererChildren = useMemo(() => renderer?.(), [renderer]);
	const isSelected = useMemo(() => {
		if (!selected && !value) return false;
		if (value?.length) return true;
		if (selected) return true;
		return false;
	}, [selected, value]);

	if (!values && !rendererChildren && !ignoreEmpty) {
		if (mode === "multiple") {
			return (
				<DropdownMenuItem
					onSelect={(event) => {
						event.preventDefault();
						onClick(ignoreEmpty ? name : undefined);
					}}
				>
					<Checkbox checked={isSelected} />
					{trigger}
				</DropdownMenuItem>
			);
		}

		return <DropdownMenuRadioItem value={id ?? name}>{trigger}</DropdownMenuRadioItem>;
	}

	const onCheckAll = useCallback(
		(e: MouseEvent<HTMLDivElement> | Event) => {
			e.preventDefault();
			onClick("all");
		},
		[onClick],
	);

	const onEmptyClick = useCallback(
		(e: Event) => {
			e.preventDefault();
			onClick("none");
		},
		[onClick],
	);

	return (
		<DropdownMenuSub>
			<DropdownMenuSubTrigger onClick={onCheckAll}>
				<Checkbox checked={isSelected} />
				{trigger}
			</DropdownMenuSubTrigger>
			<DropdownMenuSubContent>
				{(values?.length || ignoreEmpty) && (
					<PropertiesScrollContainer>
						<DropdownMenuCheckboxItem checked={getCheckedState(values, value)} onSelect={onCheckAll}>
							{t("properties.select-all")}
						</DropdownMenuCheckboxItem>
						<DropdownMenuCheckboxItem checked={!value?.includes("none")} onSelect={onEmptyClick}>
							{t("properties.empty")}
						</DropdownMenuCheckboxItem>
						<PropertiesScrollContainer>{buttons}</PropertiesScrollContainer>
						<PropertyButtons
							name={name}
							onChange={onClick}
							options={{
								closeOnSelect: true,
								invertChecked: true,
							}}
							type={mode === "single" ? "radio" : "checkbox"}
							value={value}
							values={values}
						/>
					</PropertiesScrollContainer>
				)}
				{!values?.length && rendererChildren}
			</DropdownMenuSubContent>
		</DropdownMenuSub>
	);
};

export default Item;
